import React, { useState } from 'react';
import Layout from '../../layout/Layout';
import FilterSidebarComponent from '../../components/products/FilterSidebar';
import CollectionsComponent from '../../components/products/Collections';
import ProductGridSkeleton from '../../components/shared/ProductGridSkeleton';
import ProductGridSkeletonTrigger from '../../components/shared/ProductGridSkeletonTrigger';

import { ProvideParams } from '../../modules/NextProviders';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { seo } from '../../staticData/Seo';
import { getSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { SessionState } from '../../types/state/reducers/authReducersTypes';
import NoSearchResults from '../../components/products/NoSearchResults';
import PlayVideoModal from '../../components/shared/PlayVideoModal';
import { PFLProductFilters } from '../../components/products/PFLProductFilters';
import { fetchPracticeFootageIndexProps } from '../../helpers/pages/practice-footage/index';
import { QueryParams } from '../../types/api/BasicTypes';
import axios from 'axios';
import hasSelectedFilters from '../../helpers/search/hasSelectedFilters';
import getPflFilters from '../../hooks/getPflFilters';
import { ThreeDots } from 'react-loader-spinner';

const PFL_COLLECTIONS_COUNT = 104;
import { ENV } from '../../constants/environments';

const PracticeFootageIndex: React.FC<any> = (props) => {
  const {
    collections,
    collectionOwnership,
    totalPages,
    currentPage,
    collectionParams,
    searchResultsCount,
  } = props;

  const router = useRouter();
  const { data: filters } = getPflFilters()

  const [selectedFilters, setSelectedFilters] = useState<QueryParams>({});
  const [collectionsState, setCollectionsState] = useState<Array<any>>(collections);
  const [loadingFilteredCollections, setLoadingFilteredCollections] = useState<boolean>(false);

  const [displaySkeleton, setDisplaySkeleton] = useState(false);

  const headContent = {
    title: 'Practice Footage Library',
    keywords: [
      'vfx practice footage downloads',
      'free background plates',
      'free vfx plates',
      'free vfx practice footage'
    ],
    og: {
      title: 'Practice Footage Library',
      image: seo.og.image
    },
    canonical: router.asPath.split('?')[0].replace('/', ''),
  };

  const loadFilteredCollections = async (filters: QueryParams) => {
    if (!hasSelectedFilters(filters)) {
      setCollectionsState(collections);
      return;
    }
    setLoadingFilteredCollections(true);
    try {
      const result = await axios.post(`${ENV.api.baseURL}/scenes/filter`, { filters });
      setCollectionsState(result.data.data);
      setLoadingFilteredCollections(false);
    }
    catch (e) {
      setLoadingFilteredCollections(false);
    }
  }

  const onFiltersApplied = (filters: QueryParams) => {
    setSelectedFilters(filters);
    setCollectionsState([]);
    loadFilteredCollections(filters);
    const { query } = router;
    if (query.search) {
      router.push('/practice-footage', undefined, { shallow: true });
    }
  }

  return (
    <Layout head={headContent}>
      <ProvideParams>
        {({ id, slug, query }) => (
          <div className="products-container">
            <div id="products-page" className="flex flex-col md:flex-row w-full bg-[#252A2D]">
              {
                filters?.length &&
                <FilterSidebarComponent
                  filters={filters}
                  selectedFilters={selectedFilters}
                  filterSelectionChange={onFiltersApplied}
                />
              }
              <div className="w-full md:min-w-[450px] p-6">
                <section
                  className="w-full h-[260px] flex items-center rounded-[5px] mb-5 bg-[url('/img/pfl-header.jpg')] bg-cover bg-center bg-no-repeat relative"
                  style={{
                    backgroundImage: `linear-gradient( rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6) ), url('/img/pfl-header.jpg')`
                  }}
                >
                  <div className="w-11/12 mx-auto text-center">
                    <h1 className="uppercase font-bold text-[24px] lg:text-[36px] leading-tight">
                      Browse <span className="text-a-blue">Free</span> VFX Practice Footage
                    </h1>
                    <h3 className="text-[15px] lg:text-[22px] text-a-light-gray mt-4 leading-snug inter">
                      Download movie-quality demo footage to learn VFX for free!
                    </h3>

                    <PlayVideoModal type="text" content="WHY WE DID IT" video_link="ppUQws8oPtg" />
                  </div>
                </section>

                {
                  filters?.length &&
                  <PFLProductFilters
                    defaults={collectionParams}
                    filters={filters}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={onFiltersApplied}
                  />
                }

                {collectionParams?.search?.length > 0 && !loadingFilteredCollections && (
                  <div className="text-[16px] text-gray-600 mb-8">
                    <span>
                      Showing <span className="text-white">{collectionsState.length}</span>{' '}
                      scenes of <span className="text-white">{PFL_COLLECTIONS_COUNT}</span>
                    </span>
                    <a href="/practice-footage" className="ml-4 text-white underline">
                      Clear filters
                    </a>
                  </div>
                )}

                {!collectionParams?.search && !loadingFilteredCollections &&
                  Object.keys(selectedFilters).length > 0 &&
                  collectionsState.length < PFL_COLLECTIONS_COUNT && (
                    <div className="text-[16px] text-gray-600 mb-8">
                      <span>
                        Showing <span className="text-white">{collectionsState.length}</span>{' '}
                        scenes of <span className="text-white">{PFL_COLLECTIONS_COUNT}</span>
                      </span>
                      <a
                        href="#"
                        className="ml-4 text-white underline"
                        onClick={() => {
                          setSelectedFilters({});
                        }}
                      >
                        Clear filters
                      </a>
                    </div>
                  )}

                {collectionParams.search?.length > 0 && searchResultsCount == 0 && (
                  <NoSearchResults term={collectionParams.search} />
                )}

                {
                  !displaySkeleton && !loadingFilteredCollections &&
                  <CollectionsComponent
                    id={id}
                    slug={slug || 'all'}
                    query={query}
                    name={'Practice Footage Library'}
                    collections={collectionsState}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    collectionParams={collectionParams}
                    collectionOwnership={collectionOwnership}
                    hidePagination={true}
                  />
                }
                <ProductGridSkeleton visible={displaySkeleton || loadingFilteredCollections} />
                <ProductGridSkeletonTrigger
                  setDisplaySkeleton={setDisplaySkeleton}
                />
              </div>
            </div>
          </div>
        )}
      </ProvideParams>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const session: SessionState = await getSession(ctx);
  return fetchPracticeFootageIndexProps(ctx, session);
};

export default PracticeFootageIndex;
